import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtEUR, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TrendingUp, TrendingDown, Receipt, Percent, Banknote, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { monthOptions, monthRangeFor } from "@/lib/utils";
import { toast } from "sonner";

const COLORS = ["#D9381E", "#1E293B", "#D97706"];

function toISO(d) { return d.toISOString().slice(0, 10); }

function rangeFor(period) {
  const now = new Date();
  const today = toISO(now);
  switch (period) {
    case "hoy": return [today, today];
    case "ayer": { const y = new Date(now); y.setDate(y.getDate() - 1); return [toISO(y), toISO(y)]; }
    case "semana": { const d = new Date(now); d.setDate(d.getDate() - 6); return [toISO(d), today]; }
    case "mes": return [toISO(new Date(now.getFullYear(), now.getMonth(), 1)), today];
    case "mes_anterior": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return [toISO(first), toISO(last)];
    }
    case "ytd": return [toISO(new Date(now.getFullYear(), 0, 1)), today];
    default: return [null, null];
  }
}

const PERIODS = [
  ["hoy", "Hoy"], ["ayer", "Ayer"], ["semana", "7 días"], ["mes", "Este mes"],
  ["mes_anterior", "Mes anterior"], ["ytd", "YTD"], ["custom", "Personalizado"],
];

function Kpi({ title, value, sub, variation, icon: Icon, testid }) {
  return (
    <Card data-testid={testid} className="relative overflow-hidden hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</CardTitle>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-extrabold font-num tracking-tight">{value}</div>
        <div className="flex items-center gap-2 mt-1">
          {variation !== null && variation !== undefined && (
            <Badge data-testid={`${testid}-variation`} variant="outline"
              className={variation >= 0 ? "text-green-700 border-green-200 bg-green-50" : "text-red-700 border-red-200 bg-red-50"}>
              {variation >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              {variation >= 0 ? "+" : ""}{variation}%
            </Badge>
          )}
          {sub && <span className="text-xs text-slate-500">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { branchFilter } = useAuth();
  const [period, setPeriod] = useState("hoy");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [mix, setMix] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const [dfrom, dto] = useMemo(() => {
    if (period === "custom") return [customFrom, customTo];
    return rangeFor(period);
  }, [period, customFrom, customTo]);

  const load = useCallback(() => {
    if (!dfrom || !dto) return;
    const p = new URLSearchParams({ dfrom, dto });
    if (branchFilter) p.set("branch_id", branchFilter);
    const suffix = branchFilter ? `&branch_id=${branchFilter}` : "";
    api.get(`/dashboard/summary?${p}`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/dashboard/daily?dfrom=${dfrom}&dto=${dto}${suffix}`).then((r) => setDaily(r.data)).catch(() => {});
    api.get(`/dashboard/payment-mix?dfrom=${dfrom}&dto=${dto}${suffix}`).then((r) => setMix(r.data)).catch(() => {});
  }, [dfrom, dto, branchFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/loyverse/sync/incremental");
      toast.success(`Sincronización completada: ${r.data.saved} tickets actualizados`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar con Loyverse");
    } finally {
      setSyncing(false);
    }
  };

  const c = summary?.current || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Ventas casi en tiempo real · refresco automático cada 2 min</p>
        </div>
        <Button data-testid="sync-now-button" onClick={syncNow} disabled={syncing} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          Sincronizar Loyverse
        </Button>
      </div>

      <Card data-testid="period-selector">
        <CardContent className="p-4 flex items-center gap-2 flex-wrap">
          {PERIODS.map(([key, label]) => (
            <Button key={key} data-testid={`period-${key}`} size="sm"
              variant={period === key ? "default" : "outline"}
              className={period === key ? "bg-[#D9381E] hover:bg-[#B91C1C] text-white" : ""}
              onClick={() => setPeriod(key)}>
              {label}
            </Button>
          ))}
          <Select value="" onValueChange={(ym) => { const [f, t] = monthRangeFor(ym); setPeriod("custom"); setCustomFrom(f); setCustomTo(t); }}>
            <SelectTrigger data-testid="period-month-filter" className="w-44 h-9"><SelectValue placeholder="Mes concreto..." /></SelectTrigger>
            <SelectContent>
              {monthOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Input data-testid="period-custom-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40 h-9" />
              <span className="text-slate-400 text-sm">→</span>
              <Input data-testid="period-custom-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40 h-9" />
            </div>
          )}
          {summary && (
            <span className="text-xs text-slate-400 ml-auto">
              Comparando con el mismo periodo del año anterior ({summary.prev_from} → {summary.prev_to})
            </span>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Kpi testid="kpi-net-sales" title="Ventas netas" value={fmtEUR(c.net_sales)} icon={TrendingUp}
          variation={summary?.var_net_sales} sub="vs año anterior" />
        <Kpi testid="kpi-tickets" title="Tickets" value={fmtNum(c.tickets)} icon={Receipt}
          variation={summary?.var_tickets} sub={`Ticket medio: ${fmtEUR(c.avg_ticket)}`} />
        <Kpi testid="kpi-cash" title="Efectivo" value={fmtEUR(c.cash)} icon={Banknote}
          sub={`Tarjeta: ${fmtEUR(c.card)}`} />
        <Kpi testid="kpi-discounts" title="Descuentos / Devol." value={fmtEUR((c.discounts || 0) + (c.refunds || 0))}
          icon={Percent} sub={`IVA: ${fmtEUR(c.tax)} · Propinas: ${fmtEUR(c.tips)}`} />
      </div>

      {summary?.per_branch?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="branch-comparison">
          {summary.per_branch.map((b) => {
            const v = b.prev_net_sales ? Math.round((b.net_sales - b.prev_net_sales) / Math.abs(b.prev_net_sales) * 1000) / 10 : null;
            return (
              <Card key={b.branch_id} data-testid={`branch-card-${b.branch_id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold font-heading">{b.branch_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-2xl font-extrabold font-num">{fmtEUR(b.net_sales)}</div>
                      <div className="text-xs text-slate-500 mt-1">{b.tickets} tickets · medio {fmtEUR(b.avg_ticket)}</div>
                    </div>
                    {v !== null && (
                      <Badge variant="outline" className={v >= 0 ? "text-green-700 border-green-200 bg-green-50" : "text-red-700 border-red-200 bg-red-50"}>
                        {v >= 0 ? "+" : ""}{v}% vs año ant.
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="chart-daily-sales">
          <CardHeader>
            <CardTitle className="text-base font-semibold font-heading">Ventas netas diarias</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-slate-400">
                Sin datos de ventas en este periodo.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Bar dataKey="net_sales" name="Ventas netas" fill="#D9381E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card data-testid="chart-payment-mix">
          <CardHeader>
            <CardTitle className="text-base font-semibold font-heading">Mix de pago</CardTitle>
          </CardHeader>
          <CardContent>
            {mix.every((m) => !m.value) ? (
              <div className="h-64 flex items-center justify-center text-sm text-slate-400">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={mix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
